import { isArrayWithMultiplesValues } from 'affiny-core/helpers/array';
import { logInvalidQuestion } from 'affiny-core/helpers/memberChoices';
import { getMemberChoices } from 'affiny-core/modules/api';
import {
  IMemberChoices,
  IMinMax,
} from 'affiny-core/modules/api/models/memberchoice/memberchoice.types';
import {
  patchHasCompletedProfile,
  patchMemberAnnounce,
  postHiddenProfile,
  postMemberChoices,
} from 'affiny-core/modules/api/repositories/member/member.sagas';
import { handleAsyncStatus } from 'affiny-core/modules/asynchronousState/saga';
import { EAsyncEventTypes } from 'affiny-core/modules/asynchronousState/types';
import { fetchData } from 'affiny-core/modules/auth/saga';
import { selectAccountId } from 'affiny-core/redux/selectors/accountSelectors';
import { trackEventSaga } from 'affiny-core/services/tracking/saga';
import { TrackerEvents } from 'affiny-core/services/tracking/tracker';
import isObject from 'lodash/isObject';
import mapValues from 'lodash/mapValues';
import { all, call, put, select, spawn, takeEvery } from 'redux-saga/effects';
import { EAccountActionType } from '../account/actions';
import {
  EMemberChoicesActionType,
  EValidateAnnounceActionType,
  getMemberChoicesSuccess,
  IGetMemberChoicesAction,
  IPatchAnnounceAndValidateProfileCaptureAction,
  IPostMemberChoicesAction,
  IUpdateMemberChoicesAction,
  updateMemberChoices,
  updateMemberChoicesSuccess,
} from './actions';

export function* getProfileMemberChoicesSaga(action?: IGetMemberChoicesAction) {
  const accountId = yield select(selectAccountId);
  const payloadAccountId = action?.payload?.accountId;
  const id = payloadAccountId || accountId;
  const response = yield call(getMemberChoices, id);

  yield put(
    getMemberChoicesSuccess({
      values: response,
      accountId: id,
    })
  );
}

const I_KEEP_IT_FOR_ME = 0;
const I_DO_NOT_WANT_TO_ANSWER = -1;

export function* updateMemberChoicesSaga(action: IUpdateMemberChoicesAction) {
  const { payload } = action;
  const accountId = yield select(selectAccountId);

  // Note that this is optimistic
  yield put(updateMemberChoicesSuccess(payload, accountId));
}

export function* postMemberChoicesSaga(action: IPostMemberChoicesAction) {
  const { payload } = action;
  const validatedPayload = (mapValues(payload, answer => {
    if (isArrayWithMultiplesValues(answer)) {
      const numbers = answer as number[];

      if (numbers.includes(I_KEEP_IT_FOR_ME)) {
        logInvalidQuestion(payload, numbers);

        // ignore the "keep-it-for-me" & keep all other results
        return numbers.filter(number => number !== I_KEEP_IT_FOR_ME);
      } else if (numbers.includes(I_DO_NOT_WANT_TO_ANSWER)) {
        return numbers.filter(number => number !== I_DO_NOT_WANT_TO_ANSWER);
      }
    }

    if (isObject(answer)) return answer as IMinMax;

    return answer;
  }) as any) as IMemberChoices;

  if (validatedPayload) {
    yield call(postMemberChoices, validatedPayload);
    yield put(updateMemberChoices(validatedPayload));
  }
}

export function* patchMemberAnnounceSaga(
  action: IPatchAnnounceAndValidateProfileCaptureAction
) {
  const { payload } = action;

  if (payload) {
    yield call(patchMemberAnnounce, payload);
    yield spawn(trackEventSaga, TrackerEvents.ANNOUNCE_COMPLETE);
  }
}

export function* patchHasCompletedProfileSaga() {
  yield call(postHiddenProfile);
  yield call(patchHasCompletedProfile);
  yield spawn(trackEventSaga, TrackerEvents.PROFILE_COMPLETE);
  yield call(fetchData);
}

export function* memberChoicesRootSaga() {
  yield all([
    takeEvery(
      EMemberChoicesActionType.GET,
      handleAsyncStatus(
        getProfileMemberChoicesSaga,
        EAsyncEventTypes.GET_MEMBER_CHOICES
      )
    ),
    takeEvery(EMemberChoicesActionType.UPDATE, updateMemberChoicesSaga),
    takeEvery(
      EMemberChoicesActionType.POST,
      handleAsyncStatus(
        postMemberChoicesSaga,
        EAsyncEventTypes.MEMBER_CHOICE,
        true
      )
    ),
    takeEvery(
      EValidateAnnounceActionType.PATCH,
      handleAsyncStatus(
        patchMemberAnnounceSaga,
        EAsyncEventTypes.ANNOUNCE,
        true
      )
    ),
    takeEvery(
      EAccountActionType.PATCH_COMPLETED_PROFILE,
      handleAsyncStatus(
        patchHasCompletedProfileSaga,
        EAsyncEventTypes.HAS_COMPLETED_PROFILE,
        true
      )
    ),
  ]);
}
